import { useState, useMemo, useEffect } from "react"
import { useTableSort } from "@/app/hooks/useTableSort"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { useClientTheme } from "@/app/components/ThemeContext"
import { SortIcon } from "@/app/components/SortIcon"
import { TableEmptyState, EmptyStateContent } from "@/app/components/TableEmptyState"
import { TableColumnHeader } from "@/app/components/TableColumnHeader"
import { DataCard } from "@/app/components/DataCard"
import { MobileSortSelect } from "@/app/components/MobileSortSelect"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { SearchInput } from "@/app/components/FilterBar/SearchInput"
import { MultiSelectFilter } from "@/app/components/FilterBar/MultiSelectFilter"
import { MoreFilters, MoreFiltersSection } from "@/app/components/FilterBar/MoreFilters"
import { SegmentedControl } from "@/app/components/FilterBar/SegmentedControl"
import { TogglePillGroup } from "@/app/components/FilterBar/TogglePillGroup"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { stickyTh, fitCol } from "@/app/lib/tableStyles"
import { DIV_RED } from "@/app/lib/txColors"
import { interestTypeLabel, interestTypeColor } from "@/app/lib/interestType"
import { toCsv, downloadCsv } from "@/app/lib/csv"
import { NoteIcon } from "@/app/components/NoteBadge"
import { hasKindPosition, kindNetAcres, type InventoryKinds } from "@/app/lib/batchTransactions"
import type { InterestKind } from "@/app/pages/NewTransactionPage/types"

export interface InventoryRow {
  id: string
  str: string
  section: number | null
  township_range: string
  county: string | null
  operator: string | null
  net_acres: string
  weighted_nri: string
  orri_value: string
  wells: string[]
  interest_types: string[]
  note: string | null
  /** Per-kind position slices. Absent on stale cached payloads that predate
   *  interest kinds — treat such rows as leasehold-only (see hasKindPosition).
   *  Nil server values are compacted away, so optional slice fields are
   *  simply absent. */
  kinds?: InventoryKinds
}

/** An InventoryRow projected onto the active kind: the top-level figure fields
 *  are overwritten with the kind slice's values so filtering, sorting, and
 *  rendering all read one flat shape. `kinds` is left intact for the modal. */
interface ViewRow extends InventoryRow {
  /** Acre-weighted royalty fraction; "" when absent (renders as an em-dash). */
  weighted_royalty: string
  legal_descs: string[]
  /** The kind's earliest PT expiration (ISO date), already PT-gated in the
   *  projection; "" when hidden/absent (renders as an em-dash, sorts first). */
  next_expiration: string
}

interface Props {
  data: InventoryRow[]
  // Page-level Leasehold | Mineral tab — lifted to InventoryPage so the KPI
  // cards can follow the active kind.
  kind: InterestKind
  onKindChange: (kind: InterestKind) => void
  onRowClick?: (row: InventoryRow) => void
  // Reports the filtered (pre-sort) rows up so the page's stat cards reflect
  // the active filters. Identity is stable across renders (memoized on filters).
  onFilteredChange?: (rows: InventoryRow[]) => void
  // The page-header Export CSV button lives in InventoryPage; the table owns
  // the current view (filters + sort), so it hands its export fn up via ref.
  exportRef?: React.MutableRefObject<(() => void) | null>
}

function SectionHighlight({
  value,
  query,
  theme,
}: {
  value: string
  query: string
  theme: ReturnType<typeof useClientTheme>["theme"]
}) {
  const base: React.CSSProperties = {
    fontFamily: "var(--font-data)",
    fontSize: "12px",
    fontWeight: 500,
    color: theme.textPrimary,
    letterSpacing: "0.02em",
    // STRs are hyphenated ("1-10N-5W") — never let a shrunk column break them.
    whiteSpace: "nowrap",
  }
  if (!query) return <span style={base}>{value}</span>
  const idx = value.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <span style={base}>{value}</span>
  return (
    <span style={base}>
      {value.slice(0, idx)}
      <mark
        style={{
          backgroundColor: `${theme.noteAccent}40`,
          color: theme.textPrimary,
          borderRadius: "2px",
          padding: "0 1px",
        }}
      >
        {value.slice(idx, idx + query.length)}
      </mark>
      {value.slice(idx + query.length)}
    </span>
  )
}

// Kind-scoped interest statuses for a row (stale-cache rows are
// leasehold-only, so their top-level mixed set is leasehold-accurate).
function kindInterestTypes(r: InventoryRow, k: InterestKind): string[] {
  const slice = r.kinds?.[k]
  if (slice) return slice.interest_types ?? []
  return k === "leasehold" ? r.interest_types : []
}

export function InventoryTable({
  data,
  kind,
  onKindChange,
  onRowClick,
  onFilteredChange,
  exportRef,
}: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()
  const { user } = useCurrentUser()
  const [strSearch, setStrSearch] = useState("")
  // Empty array = "All" (no constraint). A concrete array = only those values pass.
  const [operatorSel, setOperatorSel] = useState<string[]>([])
  const [countySel, setCountySel] = useState<string[]>([])
  const [townshipSel, setTownshipSel] = useState<string[]>([])
  const [sectionSel, setSectionSel] = useState<string[]>([])
  const [interestSel, setInterestSel] = useState<string[]>([])
  // "" = All, "yes" = has ≥1 well, "no" = no wells.
  const [hasWellSel, setHasWellSel] = useState("")
  // "" = All, "yes" = ORRI Val > 0, "no" = no ORRI Val.
  const [hasOrriSel, setHasOrriSel] = useState("")
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)

  // The working row set for the active tab, BEFORE any filtering: rows with a
  // position in this kind, projected onto the kind slice's figures (see
  // hasKindPosition for the stale-cache leasehold fallback).
  const kindRows = useMemo<ViewRow[]>(
    () =>
      data
        .filter((r) => hasKindPosition(r.kinds, kind))
        .map((r) => {
          const slice = r.kinds?.[kind]
          const interestTypes = kindInterestTypes(r, kind)
          return {
            ...r,
            net_acres: kindNetAcres(r, kind),
            weighted_nri: slice ? (slice.weighted_nri ?? "0") : r.weighted_nri,
            // Royalty only surfaces while the position is actually leased (PT
            // or HBP) — an Open/other status has no lease royalty to show.
            // Gate once here so the cell, mobile card, CSV, and sort all read
            // the same value (same pattern as next_expiration below).
            weighted_royalty:
              interestTypes.includes("PT") || interestTypes.includes("HBP")
                ? (slice?.weighted_royalty ?? "")
                : "",
            interest_types: interestTypes,
            legal_descs: slice?.legal_descs ?? [],
            // Expiration only surfaces while the kind still holds a
            // primary-term (PT) position — gate once here so the cell, mobile
            // card, CSV, and sort all read the same value. ISO yyyy-mm-dd
            // sorts correctly as a string; "" (hidden/absent) sorts first.
            next_expiration:
              interestTypes.includes("PT") && slice?.next_expiration ? slice.next_expiration : "",
          }
        }),
    [data, kind]
  )

  const { sortKey, sortDir, handleSort, resetSort, sortRows } = useTableSort({
    rows: kindRows,
    numericKeys:
      kind === "leasehold"
        ? ["net_acres", "weighted_nri", "orri_value"]
        : ["net_acres", "weighted_royalty"],
  })

  const switchKind = (next: InterestKind) => {
    if (next === kind) return
    onKindChange(next)
    // Has ORRI only exists on leasehold — a hidden active value would filter
    // mineral rows invisibly.
    setHasOrriSel("")
    // Prune the pill filters to values the next kind actually offers.
    // TogglePillGroup only renders the current option list, so a selection
    // that exists only in the other kind would keep filtering with no visible
    // pill anywhere. Other filters (search, dropdowns) carry across tabs —
    // their selected values stay visible even when they match nothing.
    const nextRows = data.filter((r) => hasKindPosition(r.kinds, next))
    const nextInterest = new Set(nextRows.flatMap((r) => kindInterestTypes(r, next)))
    const nextSections = new Set(
      nextRows.filter((r) => r.section !== null).map((r) => String(r.section))
    )
    setInterestSel((sel) => sel.filter((v) => nextInterest.has(v)))
    setSectionSel((sel) => sel.filter((v) => nextSections.has(v)))
    // Drop a sort keyed on a column the other tab doesn't have.
    if (sortKey && ["weighted_nri", "orri_value", "weighted_royalty"].includes(sortKey as string))
      resetSort()
  }

  // One predicate powers the table filter and every dropdown's option list.
  // `except` skips that dropdown's own constraint so its options reflect what
  // the OTHER filters allow — a selected value stays listed and broadenable.
  type FilterKey = "section" | "county" | "township" | "operator" | "interest"
  const {
    filtered,
    sectionOptions,
    countyOptions,
    townshipOptions,
    operatorOptions,
    interestOptions,
  } = useMemo(() => {
    const passes = (row: ViewRow, except?: FilterKey) => {
      const strMatch = !strSearch || row.str.toLowerCase().includes(strSearch.toLowerCase())
      const sectionMatch =
        except === "section" ||
        sectionSel.length === 0 ||
        (row.section !== null && sectionSel.includes(String(row.section)))
      const countyMatch =
        except === "county" ||
        countySel.length === 0 ||
        (row.county !== null && countySel.includes(row.county))
      const townshipMatch =
        except === "township" ||
        townshipSel.length === 0 ||
        townshipSel.includes(row.township_range)
      const operatorMatch =
        except === "operator" ||
        operatorSel.length === 0 ||
        (row.operator !== null && operatorSel.includes(row.operator))
      // An aggregated STR row carries a set of interest types; it passes when
      // any one of them is selected.
      const interestMatch =
        except === "interest" ||
        interestSel.length === 0 ||
        row.interest_types.some((it) => interestSel.includes(it))
      const hasWellMatch =
        hasWellSel === "" || (hasWellSel === "yes" ? row.wells.length > 0 : row.wells.length === 0)
      const hasOrriMatch =
        hasOrriSel === "" ||
        (hasOrriSel === "yes" ? Number(row.orri_value) > 0 : !(Number(row.orri_value) > 0))
      return (
        strMatch &&
        sectionMatch &&
        countyMatch &&
        townshipMatch &&
        operatorMatch &&
        interestMatch &&
        hasWellMatch &&
        hasOrriMatch
      )
    }
    const uniq = (vals: (string | null)[]) => Array.from(new Set(vals.filter(Boolean) as string[]))
    return {
      filtered: kindRows.filter((r) => passes(r)),
      sectionOptions: uniq(
        kindRows
          .filter((r) => passes(r, "section"))
          .map((r) => (r.section !== null ? String(r.section) : null))
      ).sort((a, b) => Number(a) - Number(b)),
      countyOptions: uniq(kindRows.filter((r) => passes(r, "county")).map((r) => r.county)).sort(),
      townshipOptions: uniq(
        kindRows.filter((r) => passes(r, "township")).map((r) => r.township_range)
      ).sort(),
      operatorOptions: uniq(
        kindRows.filter((r) => passes(r, "operator")).map((r) => r.operator)
      ).sort(),
      // interest_types on a ViewRow are already kind-scoped, so the options
      // (and the filter itself) follow the active tab.
      interestOptions: Array.from(
        new Set(kindRows.filter((r) => passes(r, "interest")).flatMap((r) => r.interest_types))
      ).sort(),
    }
  }, [
    kindRows,
    strSearch,
    sectionSel,
    countySel,
    townshipSel,
    operatorSel,
    interestSel,
    hasWellSel,
    hasOrriSel,
  ])

  // Report the filtered set up to the page so the stat cards track the filters.
  useEffect(() => {
    onFilteredChange?.(filtered)
  }, [filtered, onFilteredChange])

  const sorted = useMemo(() => sortRows(filtered), [filtered, sortRows])

  const hasFilters =
    !!strSearch ||
    sectionSel.length > 0 ||
    operatorSel.length > 0 ||
    countySel.length > 0 ||
    townshipSel.length > 0 ||
    interestSel.length > 0 ||
    hasWellSel !== "" ||
    hasOrriSel !== ""
  const clearAll = () => {
    setStrSearch("")
    setSectionSel([])
    setOperatorSel([])
    setCountySel([])
    setTownshipSel([])
    setInterestSel([])
    setHasWellSel("")
    setHasOrriSel("")
  }

  const handleExport = () => {
    const headers =
      kind === "leasehold"
        ? [
            "STR",
            "County",
            "Net Acres",
            "Weighted NRI",
            "ORRI Val",
            "Interest Status",
            "Expiration",
            "Wells",
            "Operator",
            "Notes",
          ]
        : [
            "STR",
            "County",
            "Net Acres",
            "Royalty",
            "Interest Status",
            "Expiration",
            "Legal Descs",
            "Wells",
            "Operator",
            "Notes",
          ]
    // Mirror what's on screen: the CSV is an export of the current view, so
    // each cell matches its rendered column for the active tab (Net Acres 4dp,
    // NRI as a percent, royalty as a 4dp fraction, blanks where the UI shows —).
    const rows = sorted.map((r) => {
      const shared = [r.str, r.county ?? "", formatNumber(r.net_acres, 4)]
      const tail = [r.wells.join("; "), r.operator ?? "", r.note ?? ""]
      if (kind === "leasehold") {
        const orriNum = Number(r.orri_value)
        return [
          ...shared,
          decimalToPercent(r.weighted_nri, 2),
          orriNum > 0 ? formatNumber(orriNum, 4) : "",
          r.interest_types.join("; "),
          r.next_expiration,
          ...tail,
        ]
      }
      return [
        ...shared,
        r.weighted_royalty !== "" ? formatNumber(r.weighted_royalty, 4) : "",
        r.interest_types.join("; "),
        r.next_expiration,
        r.legal_descs.join("; "),
        ...tail,
      ]
    })
    // "Cherokee Moon - 7-3-2026.csv"
    const clientName = user?.client?.name || theme.clientName
    const date = new Date().toLocaleDateString("en-US").replace(/\//g, "-")
    downloadCsv(`${clientName} - ${date}.csv`, toCsv(headers, rows))
  }

  // Hand the export fn (bound to the current filters + sort) up to the page,
  // where the header button lives.
  useEffect(() => {
    if (!exportRef) return
    exportRef.current = handleExport
    return () => {
      exportRef.current = null
    }
  })

  return (
    <div>
      {/* Interest-kind tabs, styled like the New Transaction page's tab bar. */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <SegmentedControl
          value={kind}
          ariaLabel="Interest kind"
          accents={theme.kindAccents}
          onChange={(next) => switchKind(next as InterestKind)}
          options={[
            { label: "Leasehold", value: "leasehold" },
            { label: "Mineral", value: "mineral" },
          ]}
          theme={theme}
        />
      </div>

      <div
        className="overflow-hidden"
        style={{
          border: `1px solid ${theme.borderColor}`,
          borderRadius: "8px",
          backgroundColor: theme.bgSurface,
        }}
      >
        <FilterBar
          hasFilters={hasFilters}
          onClear={clearAll}
          resultCount={sorted.length}
          totalCount={kindRows.length}
          entityLabel="holding"
          theme={theme}
        >
          <SearchInput
            value={strSearch}
            onChange={setStrSearch}
            placeholder="Search S-T-R…"
            width="180px"
            theme={theme}
          />
          <MultiSelectFilter
            selected={townshipSel}
            onChange={setTownshipSel}
            placeholder="All Township-Ranges"
            options={townshipOptions}
            width="190px"
            theme={theme}
          />
          <MultiSelectFilter
            selected={countySel}
            onChange={setCountySel}
            placeholder="All Counties"
            options={countyOptions}
            theme={theme}
          />
          <MultiSelectFilter
            selected={operatorSel}
            onChange={setOperatorSel}
            placeholder="All Operators"
            options={operatorOptions}
            width="200px"
            theme={theme}
          />
          {/* Less-used filters live behind the overflow popover. */}
          <MoreFilters
            activeCount={
              [
                sectionSel.length > 0,
                interestSel.length > 0,
                hasWellSel !== "",
                hasOrriSel !== "",
              ].filter(Boolean).length
            }
            theme={theme}
          >
            <MoreFiltersSection label="Section" theme={theme}>
              <TogglePillGroup
                selected={sectionSel}
                onChange={setSectionSel}
                options={sectionOptions}
                pillMinWidth="36px"
                theme={theme}
              />
            </MoreFiltersSection>
            <MoreFiltersSection label="Interest Status" theme={theme}>
              <TogglePillGroup
                selected={interestSel}
                onChange={setInterestSel}
                options={interestOptions}
                theme={theme}
              />
            </MoreFiltersSection>
            <MoreFiltersSection label="Well" theme={theme}>
              <SegmentedControl
                value={hasWellSel}
                onChange={setHasWellSel}
                options={[
                  { label: "All", value: "" },
                  { label: "Has Well", value: "yes" },
                  { label: "No Well", value: "no" },
                ]}
                theme={theme}
              />
            </MoreFiltersSection>
            {/* ORRI only exists on leasehold rows — hide the filter on mineral
            (switchKind clears any active value). */}
            {kind === "leasehold" && (
              <MoreFiltersSection label="ORRI" theme={theme}>
                <SegmentedControl
                  value={hasOrriSel}
                  onChange={setHasOrriSel}
                  options={[
                    { label: "All", value: "" },
                    { label: "Has ORRI", value: "yes" },
                    { label: "No ORRI", value: "no" },
                  ]}
                  theme={theme}
                />
              </MoreFiltersSection>
            )}
          </MoreFilters>
        </FilterBar>

        {/* Mobile: card reflow. Desktop: full table. */}
        {isMobile ? (
          sorted.length === 0 ? (
            <div className="px-3.5 py-10">
              <EmptyStateContent
                message="No holdings match your filters"
                onClear={clearAll}
                theme={theme}
              />
            </div>
          ) : (
            <>
              <MobileSortSelect
                options={[
                  { key: "str", label: "STR" },
                  { key: "county", label: "County" },
                  { key: "net_acres", label: "Net Acres" },
                  ...(kind === "leasehold"
                    ? [
                        { key: "weighted_nri", label: "Wtd NRI" },
                        { key: "orri_value", label: "ORRI Val" },
                      ]
                    : [{ key: "weighted_royalty", label: "Royalty" }]),
                  { key: "next_expiration", label: "Expiration" },
                  { key: "operator", label: "Operator" },
                ]}
                sortKey={sortKey as string | null}
                sortDir={sortDir}
                onSort={(k) => handleSort(k as keyof ViewRow)}
              />
              <div className="flex flex-col gap-2.5 p-3">
                {sorted.map((row) => {
                  const orriNum = Number(row.orri_value)
                  return (
                    <DataCard
                      key={row.id}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      title={
                        <span className="inline-flex items-center gap-1.5">
                          <SectionHighlight value={row.str} query={strSearch} theme={theme} />
                          {row.note ? <NoteIcon note={row.note} theme={theme} /> : null}
                        </span>
                      }
                      accessory={
                        <span
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "11px",
                            color: theme.textMuted,
                          }}
                        >
                          {row.township_range}
                        </span>
                      }
                      fields={[
                        { label: "County", value: row.county ?? "—" },
                        { label: "Operator", value: row.operator ?? "—" },
                        { label: "Net Acres", value: formatNumber(row.net_acres, 4), mono: true },
                        ...(kind === "leasehold"
                          ? [
                              {
                                label: "Wtd NRI",
                                value: decimalToPercent(row.weighted_nri, 2),
                                mono: true,
                              },
                              {
                                label: "ORRI Val",
                                value: orriNum > 0 ? formatNumber(orriNum, 4) : "—",
                                mono: true,
                              },
                            ]
                          : [
                              {
                                label: "Royalty",
                                value:
                                  row.weighted_royalty !== ""
                                    ? formatNumber(row.weighted_royalty, 4)
                                    : "—",
                                mono: true,
                              },
                            ]),
                        {
                          label: "Interest Status",
                          value: row.interest_types.length ? row.interest_types.join(", ") : "—",
                        },
                        {
                          label: "Expiration",
                          value: row.next_expiration || "—",
                          mono: true,
                        },
                        ...(kind === "mineral"
                          ? [
                              {
                                label: "Legal Desc(s)",
                                full: true,
                                value: row.legal_descs.length ? row.legal_descs.join(", ") : "—",
                              },
                            ]
                          : []),
                        {
                          label: "Well(s)",
                          full: true,
                          value: row.wells.length === 0 ? "—" : row.wells.join(", "),
                        },
                      ]}
                    />
                  )
                })}
              </div>
            </>
          )
        ) : (
          <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
            <table className="w-full border-collapse">
              <thead>
                <tr
                  style={{
                    backgroundColor: theme.bgSurface2,
                    borderBottom: `1px solid ${theme.borderColor}`,
                  }}
                >
                  <th className="px-3.5 py-2 text-left" style={{ ...stickyTh(theme), ...fitCol }}>
                    <button className="flex items-center gap-1" onClick={() => handleSort("str")}>
                      <TableColumnHeader label="STR" theme={theme} />
                      <SortIcon col="str" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  <th className="px-3.5 py-2 text-left" style={{ ...stickyTh(theme), ...fitCol }}>
                    <button
                      className="flex items-center gap-1"
                      onClick={() => handleSort("county")}
                    >
                      <TableColumnHeader label="County" theme={theme} />
                      <SortIcon col="county" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  <th className="px-3.5 py-2 text-right" style={{ ...stickyTh(theme), ...fitCol }}>
                    <button
                      className="flex items-center gap-1 ml-auto"
                      onClick={() => handleSort("net_acres")}
                    >
                      <TableColumnHeader label="Net Acres" theme={theme} />
                      <SortIcon col="net_acres" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  {kind === "leasehold" ? (
                    <>
                      <th
                        className="px-3.5 py-2 text-right"
                        style={{ ...stickyTh(theme), ...fitCol }}
                      >
                        <button
                          className="flex items-center gap-1 ml-auto"
                          onClick={() => handleSort("weighted_nri")}
                        >
                          <TableColumnHeader label="Wtd NRI" theme={theme} />
                          <SortIcon
                            col="weighted_nri"
                            sortKey={sortKey}
                            sortDir={sortDir}
                            theme={theme}
                          />
                        </button>
                      </th>
                      <th
                        className="px-3.5 py-2 text-right"
                        style={{ ...stickyTh(theme), ...fitCol }}
                      >
                        <button
                          className="flex items-center gap-1 ml-auto"
                          onClick={() => handleSort("orri_value")}
                        >
                          <TableColumnHeader label="ORRI Val" theme={theme} />
                          <SortIcon
                            col="orri_value"
                            sortKey={sortKey}
                            sortDir={sortDir}
                            theme={theme}
                          />
                        </button>
                      </th>
                    </>
                  ) : (
                    <th
                      className="px-3.5 py-2 text-right"
                      style={{ ...stickyTh(theme), ...fitCol }}
                    >
                      <button
                        className="flex items-center gap-1 ml-auto"
                        onClick={() => handleSort("weighted_royalty")}
                      >
                        <TableColumnHeader label="Royalty" theme={theme} />
                        <SortIcon
                          col="weighted_royalty"
                          sortKey={sortKey}
                          sortDir={sortDir}
                          theme={theme}
                        />
                      </button>
                    </th>
                  )}
                  {/* Non-sortable (array-valued); 12px spacer keeps the label
                  aligned with the sortable headers, matching Well(s). */}
                  <th
                    className="px-3.5 py-2 text-left"
                    title="Interest Status"
                    style={{ ...stickyTh(theme), ...fitCol }}
                  >
                    <span className="flex items-center gap-1">
                      <TableColumnHeader label="Status" theme={theme} />
                      <span style={{ width: "12px", flexShrink: 0 }} aria-hidden />
                    </span>
                  </th>
                  {/* Earliest PT lease expiration for the kind (both tabs). */}
                  <th className="px-3.5 py-2 text-left" style={{ ...stickyTh(theme), ...fitCol }}>
                    <button
                      className="flex items-center gap-1"
                      onClick={() => handleSort("next_expiration")}
                    >
                      <TableColumnHeader label="Expiration" theme={theme} />
                      <SortIcon
                        col="next_expiration"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        theme={theme}
                      />
                    </button>
                  </th>
                  {/* Non-sortable (array-valued), mineral only. */}
                  {kind === "mineral" && (
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <span className="flex items-center gap-1">
                        <TableColumnHeader label="Legal Desc(s)" theme={theme} />
                        <span style={{ width: "12px", flexShrink: 0 }} aria-hidden />
                      </span>
                    </th>
                  )}
                  <th className="px-3.5 py-2 text-left" style={{ ...stickyTh(theme), ...fitCol }}>
                    <button
                      className="flex items-center gap-1"
                      onClick={() => handleSort("operator")}
                    >
                      <TableColumnHeader label="Operator" theme={theme} />
                      <SortIcon col="operator" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  {/* Non-sortable; an invisible 12px spacer matches the sort-arrow
                  width on the other headers so this label lines up with them. */}
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <span className="flex items-center gap-1">
                      <TableColumnHeader label="Well(s)" theme={theme} />
                      <span style={{ width: "12px", flexShrink: 0 }} aria-hidden />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <TableEmptyState
                    message="No holdings match your filters"
                    onClear={clearAll}
                    colSpan={9}
                    theme={theme}
                  />
                ) : (
                  sorted.map((row, index) => {
                    const orriNum = Number(row.orri_value)
                    return (
                      <tr
                        key={row.id}
                        onClick={() => onRowClick?.(row)}
                        onMouseEnter={() => setHoveredRow(index)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{
                          borderBottom: `1px solid ${theme.borderSubtle}`,
                          backgroundColor:
                            hoveredRow === index ? theme.bgSurface2 : theme.bgSurface,
                          transition: "background-color 120ms ease",
                          cursor: onRowClick ? "pointer" : "default",
                        }}
                      >
                        <td className="px-3.5 py-2" title="View STR summary">
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              style={{
                                borderBottom: `1px dotted ${theme.brandPrimary}`,
                                paddingBottom: "1px",
                              }}
                            >
                              <SectionHighlight value={row.str} query={strSearch} theme={theme} />
                            </span>
                            {row.note ? <NoteIcon note={row.note} theme={theme} /> : null}
                          </span>
                        </td>
                        <td className="px-3.5 py-2">
                          <span
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "12px",
                              color: theme.textMuted,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.county ?? "—"}
                          </span>
                        </td>
                        <td className="px-3.5 py-2 text-right">
                          <span
                            style={{
                              fontFamily: "var(--font-data)",
                              fontSize: "12px",
                              fontWeight: 500,
                              color: Number(row.net_acres) < 0 ? DIV_RED : theme.textPrimary,
                            }}
                          >
                            {formatNumber(row.net_acres, 4)}
                          </span>
                        </td>
                        {kind === "leasehold" ? (
                          <>
                            <td className="px-3.5 py-2 text-right">
                              <span
                                style={{
                                  fontFamily: "var(--font-data)",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: theme.textPrimary,
                                }}
                              >
                                {decimalToPercent(row.weighted_nri, 2)}
                              </span>
                            </td>
                            <td className="px-3.5 py-2 text-right">
                              <span
                                style={{
                                  fontFamily: "var(--font-data)",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: orriNum > 0 ? theme.textMuted : theme.textDim,
                                }}
                              >
                                {orriNum > 0 ? formatNumber(orriNum, 4) : "—"}
                              </span>
                            </td>
                          </>
                        ) : (
                          <td className="px-3.5 py-2 text-right">
                            <span
                              style={{
                                fontFamily: "var(--font-data)",
                                fontSize: "12px",
                                fontWeight: 500,
                                color:
                                  row.weighted_royalty !== "" ? theme.textPrimary : theme.textDim,
                              }}
                            >
                              {row.weighted_royalty !== ""
                                ? formatNumber(row.weighted_royalty, 4)
                                : "—"}
                            </span>
                          </td>
                        )}
                        <td className="px-3.5 py-2">
                          {row.interest_types.length === 0 ? (
                            <span
                              style={{
                                fontFamily: "var(--font-ui)",
                                fontSize: "12px",
                                color: theme.textDim,
                              }}
                            >
                              —
                            </span>
                          ) : (
                            <span className="inline-flex flex-wrap gap-1">
                              {row.interest_types.map((it) => {
                                const accent = interestTypeColor(it)
                                return (
                                  <span
                                    key={it}
                                    className="inline-block px-2 py-0.5 rounded-full"
                                    title={interestTypeLabel(it)}
                                    style={{
                                      fontFamily: "var(--font-ui)",
                                      fontSize: "11px",
                                      fontWeight: 500,
                                      color: accent ?? theme.textMuted,
                                      backgroundColor: accent ? `${accent}1A` : theme.bgSurface2,
                                      border: `1px solid ${accent ? `${accent}33` : theme.borderColor}`,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {it}
                                  </span>
                                )
                              })}
                            </span>
                          )}
                        </td>
                        <td className="px-3.5 py-2">
                          <span
                            style={{
                              fontFamily: "var(--font-data)",
                              fontSize: "12px",
                              color: row.next_expiration ? theme.textPrimary : theme.textDim,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.next_expiration || "—"}
                          </span>
                        </td>
                        {kind === "mineral" && (
                          <td className="px-3.5 py-2">
                            {row.legal_descs.length === 0 ? (
                              <span
                                style={{
                                  fontFamily: "var(--font-ui)",
                                  fontSize: "12px",
                                  color: theme.textDim,
                                }}
                              >
                                —
                              </span>
                            ) : (
                              // Comma-joined like Well(s); long lists wrap at
                              // tract boundaries within the column's width.
                              <span
                                style={{
                                  fontFamily: "var(--font-ui)",
                                  fontSize: "12px",
                                  color: theme.textPrimary,
                                  lineHeight: 1.5,
                                }}
                              >
                                {row.legal_descs.join(", ")}
                              </span>
                            )}
                          </td>
                        )}
                        <td className="px-3.5 py-2">
                          <span
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "12px",
                              color: theme.textPrimary,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.operator ?? "—"}
                          </span>
                        </td>
                        <td className="px-3.5 py-2">
                          {row.wells.length === 0 ? (
                            <span
                              style={{
                                fontFamily: "var(--font-ui)",
                                fontSize: "12px",
                                color: theme.textDim,
                              }}
                            >
                              —
                            </span>
                          ) : (
                            // Comma-joined on one line; long lists wrap at well
                            // boundaries within the column's surplus width.
                            <span
                              style={{
                                fontFamily: "var(--font-ui)",
                                fontSize: "12px",
                                color: theme.textPrimary,
                                lineHeight: 1.5,
                              }}
                            >
                              {row.wells.join(", ")}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div
          className="flex items-center justify-between px-3.5 py-3 border-t"
          style={{ borderTopColor: theme.borderColor, backgroundColor: theme.bgSurface2 }}
        >
          <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
            {hasFilters
              ? `Showing ${sorted.length} of ${kindRows.length} holdings`
              : `${kindRows.length} total holdings`}
          </span>
        </div>
      </div>
    </div>
  )
}
